import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, Download, Receipt, X, ArrowUpDown, ArrowUp, ArrowDown, Camera, FileText, BadgeDollarSign, Edit2, Calendar, Car } from 'lucide-react';
import { format } from 'date-fns';

export default function ExpensePanel({ user }) {
  const [expenseList, setExpenseList] = useState([]);
  const [expenseVendors, setExpenseVendors] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isManageVendorsOpen, setIsManageVendorsOpen] = useState(false);
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorCategory, setNewVendorCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  
  // Tax Year selector
  const currentYear = new Date().getFullYear();
  const [selectedTaxYear, setSelectedTaxYear] = useState('all');
  const availableTaxYears = ['all', currentYear.toString(), (currentYear - 1).toString(), (currentYear - 2).toString()];
  
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    vendor: '',
    category: '',
    amount: '',
    notes: ''
  });
  const [uploadingFile, setUploadingFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showReceiptViewer, setShowReceiptViewer] = useState(false);
  const [currentReceiptUrl, setCurrentReceiptUrl] = useState('');
  const [currentReceiptFilename, setCurrentReceiptFilename] = useState('');
  const [editMiles, setEditMiles] = useState('');
  const [editMileageRate, setEditMileageRate] = useState(0.70);
  const [isMileageExpense, setIsMileageExpense] = useState(false);

  const canEdit = user?.role === 'owner' || user?.role === 'super_admin' || (user?.permissions?.can_edit_expenses);
  const isOwner = user?.role === 'owner' || user?.role === 'super_admin';

  useEffect(() => {
    fetchExpenses();
    fetchExpenseVendors();
    fetchExpenseCategories();
  }, []);

  const fetchExpenseVendors = async () => {
    try {
      const response = await axios.get(`${API}/expense-vendors`);
      setExpenseVendors(response.data);
    } catch (error) {
      toast.error('Failed to fetch expense vendors');
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`);
      setExpenseCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch expense categories');
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenseList(response.data);
    } catch (error) {
      toast.error('Failed to fetch expense data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canEdit) {
      toast.error('You do not have permission to add/edit expenses');
      return;
    }

    try {
      let expenseId;
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, formData);
        expenseId = editingExpense.id;
        toast.success('Expense updated successfully');
      } else {
        const response = await axios.post(`${API}/expenses`, formData);
        expenseId = response.data.id;
        toast.success('Expense added successfully');
      }

      // Upload file if selected
      if (selectedFile && expenseId) {
        await handleFileUploadForExpense(expenseId, selectedFile);
      }

      setIsDialogOpen(false);
      setEditingExpense(null);
      setSelectedFile(null);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleFileUploadForExpense = async (expenseId, file) => {
    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      await axios.post(`${API}/upload/expense/${expenseId}`, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Receipt uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload receipt');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!canEdit) {
      toast.error('You do not have permission to delete expenses');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this expense entry?')) return;

    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date,
      vendor: expense.vendor,
      category: expense.category,
      amount: expense.amount,
      notes: expense.notes || ''
    });
    
    // Detect mileage expense and parse miles/rate from notes
    const mileageMatch = (expense.notes || '').match(/\((\d+\.?\d*)\s*miles?\s*@\s*\$(\d+\.?\d*)\/mi\)/);
    if (mileageMatch) {
      setIsMileageExpense(true);
      setEditMiles(mileageMatch[1]);
      setEditMileageRate(parseFloat(mileageMatch[2]));
    } else {
      setIsMileageExpense(false);
      setEditMiles('');
    }
    
    setIsDialogOpen(true);
  };

  const handleMilesChange = (newMiles) => {
    setEditMiles(newMiles);
    const miles = parseFloat(newMiles) || 0;
    const newAmount = (miles * editMileageRate).toFixed(2);
    // Update amount and notes with new miles
    const updatedNotes = formData.notes.replace(
      /\([\d.]+\s*miles?\s*@\s*\$[\d.]+\/mi\)/,
      `(${miles.toFixed(2)} miles @ $${editMileageRate}/mi)`
    );
    setFormData({ ...formData, amount: newAmount, notes: updatedNotes });
  };


  const handleFileUpload = async (expenseId, file) => {
    if (!canEdit) {
      toast.error('You do not have permission to upload files');
      return;
    }

    setUploadingFile(expenseId);
    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      await axios.post(`${API}/upload/expense/${expenseId}`, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Receipt uploaded successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleFileDownload = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/download/${fileId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const handleViewReceipt = async (fileId, filename) => {
    try {
      // Fetch the file with authentication
      const response = await axios.get(`${API}/download/${fileId}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Determine MIME type based on filename
      const isPdf = filename && filename.toLowerCase().endsWith('.pdf');
      const mimeType = isPdf ? 'application/pdf' : 'image/jpeg';
      
      // Create a blob URL with correct MIME type
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      setCurrentReceiptUrl(blobUrl);
      setCurrentReceiptFilename(filename || '');
      setShowReceiptViewer(true);
    } catch (error) {
      toast.error('Failed to load receipt');
    }
  };

  const resetForm = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      vendor: '',
      category: '',
      amount: '',
      notes: ''
    });
  };

  const handleAddVendor = async () => {
    if (!newVendorName.trim()) {
      toast.error('Please enter a vendor name');
      return;
    }
    if (!newVendorCategory) {
      toast.error('Please select a category for the vendor');
      return;
    }

    try {
      await axios.post(`${API}/expense-vendors`, { 
        name: newVendorName.trim(),
        category: newVendorCategory
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Vendor added successfully');
      const addedVendorName = newVendorName.trim();
      setNewVendorName('');
      setNewVendorCategory('');
      setIsManageVendorsOpen(false);
      await fetchExpenseVendors();
      // Auto-select the newly added vendor
      setFormData({ ...formData, vendor: addedVendorName });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vendor');
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this expense vendor?')) return;

    try {
      await axios.delete(`${API}/expense-vendors/${vendorId}`);
      toast.success('Expense vendor deleted successfully');
      fetchExpenseVendors();
    } catch (error) {
      toast.error('Failed to delete expense vendor');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      await axios.post(`${API}/expense-categories`, { name: newCategoryName.trim() });
      toast.success('Expense category added successfully');
      setNewCategoryName('');
      fetchExpenseCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this expense category?')) return;

    try {
      await axios.delete(`${API}/expense-categories/${categoryId}`);
      toast.success('Expense category deleted successfully');
      fetchExpenseCategories();
    } catch (error) {
      toast.error('Failed to delete expense category');
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedExpenseList = () => {
    // Filter out Tax Deposit entries - they're payments, not expenses
    let filteredList = expenseList.filter(item => item.category !== 'Tax Deposit');
    
    // Filter by selected tax year
    if (selectedTaxYear !== 'all') {
      filteredList = filteredList.filter(expense => {
        if (!expense.date) return false;
        return expense.date.startsWith(selectedTaxYear);
      });
    }
    
    const sorted = [...filteredList].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';

      // Sort by amount (number)
      if (sortConfig.key === 'amount') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Sort by date
      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'asc' 
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      // Sort by string (vendor, category, notes)
      const comparison = aValue.toString().localeCompare(bValue.toString());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Calculate totals based on selected year filter
  const getFilteredExpenses = () => {
    let filtered = expenseList;
    if (selectedTaxYear !== 'all') {
      filtered = filtered.filter(expense => expense.date && expense.date.startsWith(selectedTaxYear));
    }
    return filtered;
  };
  const filteredExpenses = getFilteredExpenses();
  const businessExpenses = filteredExpenses.filter(item => item.category !== 'Tax Deposits');
  const taxPayments = filteredExpenses.filter(item => item.category === 'Tax Deposits');
  const totalExpenses = businessExpenses.reduce((sum, item) => sum + item.amount, 0);
  const totalTaxPayments = taxPayments.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="space-y-6" data-testid="expense-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-3xl font-bold text-slate-900">Expenses</h2>
          <p className="text-xs md:text-base text-slate-600 mt-1">Track your business costs</p>
        </div>
        <div className="flex items-center gap-2 md:gap-4 flex-wrap">
          {/* Tax Year Selector */}
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-1.5 border border-slate-700">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <span className="text-xs md:text-sm text-slate-400">Year:</span>
            <Select value={selectedTaxYear} onValueChange={setSelectedTaxYear}>
              <SelectTrigger className="w-20 md:w-24 h-8 border-0 bg-transparent text-cyan-400 font-medium focus:ring-0 text-xs md:text-sm" data-testid="expense-year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTaxYears.map(year => (
                  <SelectItem key={year} value={year}>
                    {year === 'all' ? 'All Years' : year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {canEdit && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingExpense(null);
              setSelectedFile(null);
              setIsMileageExpense(false);
              setEditMiles('');
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-xs md:text-sm" data-testid="add-expense-button">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add</span> Expense
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="expense-dialog">
              <DialogHeader>
                <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
                <DialogDescription>Enter expense details below</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    data-testid="expense-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor (Optional)</Label>
                  <Select
                    value={formData.vendor}
                    onValueChange={(value) => {
                      if (value === '__add_new__') {
                        setIsManageVendorsOpen(true);
                      } else {
                        // Find the selected vendor and auto-populate its category
                        const selectedVendor = expenseVendors.find(v => v.name === value);
                        setFormData({ 
                          ...formData, 
                          vendor: value,
                          category: selectedVendor?.category || formData.category
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="expense-vendor-select">
                      <SelectValue placeholder="Select expense vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseVendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.name} data-testid={`vendor-option-${vendor.name}`}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="text-blue-600 font-medium">
                        + Add New Vendor
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger data-testid="expense-category-select">
                      <SelectValue placeholder="Select expense category" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((category) => (
                        <SelectItem key={category.id} value={category.name} data-testid={`expense-category-option-${category.name}`}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isMileageExpense && editingExpense && (
                  <div className="space-y-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <Label htmlFor="miles" className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-blue-400" />
                      Miles Driven
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="miles"
                        data-testid="expense-miles-input"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={editMiles}
                        onChange={(e) => handleMilesChange(e.target.value)}
                      />
                      <span className="text-xs text-slate-400 whitespace-nowrap">@ ${editMileageRate}/mi</span>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    data-testid="expense-amount-input"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="expense-notes-input"
                    placeholder="Additional notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receipt-upload">Receipt/Invoice (Optional)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="receipt-upload"
                      data-testid="expense-receipt-upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="flex-1"
                    />
                    <label htmlFor="expense-camera-capture">
                      <input
                        id="expense-camera-capture"
                        data-testid="expense-camera-capture"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        as="span"
                        className="gap-1"
                      >
                        <Camera className="w-4 h-4" />
                        Photo
                      </Button>
                    </label>
                    {selectedFile && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedFile(null)}
                        data-testid="clear-receipt"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  {selectedFile && (
                    <p className="text-sm text-slate-600">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                  {editingExpense?.receipt_filename && !selectedFile && (
                    <p className="text-sm text-emerald-600">
                      Current receipt: {editingExpense.receipt_filename}
                    </p>
                  )}
                </div>
                <Button type="submit" className="w-full" data-testid="expense-submit-button">
                  {editingExpense ? 'Update Expense' : 'Add Expense'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Business Expenses Panel */}
      <Card className="border-0 shadow-lg" data-testid="expense-summary-card">
        <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-800">Business Expenses</CardTitle>
              <CardDescription className="text-slate-600">{selectedTaxYear === 'all' ? 'All time' : selectedTaxYear} deductible costs</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Receipt className="w-8 h-8 text-red-600" />
              <span className="text-3xl font-bold text-red-700" data-testid="total-expenses">
                ${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card View */}
          <div className="md:hidden">
            {businessExpenses.length === 0 ? (
              <div className="text-center py-8 px-4 text-slate-500">
                No business expense entries yet. {canEdit && 'Click "Add Expense" to get started.'}
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {getSortedExpenseList().map((expense) => (
                  <Card key={expense.id} className="border-l-4 border-l-red-500" data-testid={`expense-row-${expense.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">{expense.vendor}</div>
                          <div className="text-xs text-slate-500">{expense.date}</div>
                        </div>
                        <div className="text-lg font-bold text-red-600">
                          ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-600">Category:</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {expense.category || 'Uncategorized'}
                          </span>
                        </div>
                        {expense.notes && (
                          <div className="text-xs text-slate-600">
                            <span className="font-medium">Notes:</span> {expense.notes}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                        {expense.receipt_filename && expense.receipt_filename !== 'None' ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewReceipt(expense.receipt_file_id, expense.receipt_filename)}
                              data-testid={`view-receipt-${expense.id}`}
                              className="flex-1"
                              title="View receipt"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleFileDownload(expense.receipt_file_id, expense.receipt_filename)}
                              data-testid={`download-receipt-${expense.id}`}
                              className="flex-1"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </>
                        ) : canEdit ? (
                          <label className="flex-1">
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => handleFileUpload(expense.id, e.target.files[0])}
                              disabled={uploadingFile === expense.id}
                              data-testid={`upload-receipt-${expense.id}`}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={uploadingFile === expense.id}
                              as="span"
                              className="w-full"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Upload
                            </Button>
                          </label>
                        ) : null}
                        
                        {canEdit && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(expense)}
                              data-testid={`edit-expense-${expense.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(expense.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-expense-${expense.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center">
                      Date
                      <SortIcon column="date" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('vendor')}
                  >
                    <div className="flex items-center">
                      Vendor
                      <SortIcon column="vendor" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center">
                      Category
                      <SortIcon column="category" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center justify-end">
                      Amount
                      <SortIcon column="amount" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSort('notes')}
                  >
                    <div className="flex items-center">
                      Notes
                      <SortIcon column="notes" />
                    </div>
                  </TableHead>
                  <TableHead>Receipt</TableHead>
                  {canEdit && <TableHead className="text-center">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {businessExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-slate-500">
                      No business expense entries yet. {canEdit && 'Click "Add Expense" to get started.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  getSortedExpenseList().reduce((rows, expense, index, array) => {
                    const currentYear = expense.date ? expense.date.substring(0, 4) : '';
                    const prevYear = index > 0 && array[index - 1].date ? array[index - 1].date.substring(0, 4) : currentYear;
                    
                    // Add year divider if year changes
                    if (index > 0 && currentYear !== prevYear) {
                      rows.push(
                        <TableRow key={`year-divider-${currentYear}-${index}`} className="bg-transparent">
                          <TableCell colSpan={canEdit ? 7 : 6} className="py-2 px-0">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
                              <span className="text-xs font-semibold text-cyan-400 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                                {currentYear}
                              </span>
                              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    rows.push(
                    <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                      <TableCell className="font-medium">{expense.date}</TableCell>
                      <TableCell>{expense.vendor}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {expense.category || 'Uncategorized'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-red-600">
                        ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{expense.notes || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {expense.receipt_filename && expense.receipt_filename !== 'None' ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewReceipt(expense.receipt_file_id, expense.receipt_filename)}
                                data-testid={`view-receipt-${expense.id}`}
                                title="View receipt"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFileDownload(expense.receipt_file_id, expense.receipt_filename)}
                                data-testid={`download-receipt-${expense.id}`}
                                title="Download receipt"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(expense)}
                              data-testid={`edit-expense-${expense.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(expense.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-expense-${expense.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                    return rows;
                  }, [])
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Tax Deposits Panel */}
      {taxPayments.length > 0 && (
        <Card className="border-0 shadow-lg mt-6">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-green-900">Tax Deposits</CardTitle>
                <CardDescription>Tax payments (not deductible)</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <BadgeDollarSign className="w-8 h-8 text-green-600" />
                <span className="text-3xl font-bold text-green-900">
                  ${totalTaxPayments.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Mobile Card View */}
            <div className="md:hidden">
              {taxPayments.map((expense) => (
                <Card key={expense.id} className="m-4 bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">{expense.vendor}</div>
                        <div className="text-sm text-green-700 font-medium">{expense.category}</div>
                        <div className="text-xs text-slate-500">{expense.date}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-700">
                          ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                    {expense.notes && (
                      <div className="text-xs text-slate-600 mt-2 border-t pt-2">{expense.notes}</div>
                    )}
                    {canEdit && (
                      <div className="flex gap-2 mt-3 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditExpense(expense)}
                          className="flex-1"
                        >
                          <Edit2 className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="flex-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow className="bg-green-100/50">
                    <TableHead className="cursor-pointer hover:bg-green-50" onClick={() => handleSort('date')}>
                      <div className="flex items-center">
                        Date
                        <SortIcon column="date" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-green-50" onClick={() => handleSort('vendor')}>
                      <div className="flex items-center">
                        Vendor
                        <SortIcon column="vendor" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-green-50" onClick={() => handleSort('category')}>
                      <div className="flex items-center">
                        Category
                        <SortIcon column="category" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-green-50 text-right" onClick={() => handleSort('amount')}>
                      <div className="flex items-center justify-end">
                        Amount
                        <SortIcon column="amount" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-green-50" onClick={() => handleSort('notes')}>
                      <div className="flex items-center">
                        Notes
                        <SortIcon column="notes" />
                      </div>
                    </TableHead>
                    <TableHead>Receipt</TableHead>
                    {canEdit && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxPayments.map((expense) => (
                    <TableRow key={expense.id} className="hover:bg-green-50/30">
                      <TableCell>{expense.date}</TableCell>
                      <TableCell className="font-medium">{expense.vendor}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {expense.category}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-700">
                        ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-slate-600">{expense.notes}</TableCell>
                      <TableCell>
                        {expense.receipt_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewReceipt(expense.receipt_url)}
                            className="h-8 px-2"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditExpense(expense)}
                              className="h-8 px-2"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteExpense(expense.id)}
                              className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manage dialogs removed - only Super Admin can manage vendors/categories through Employment Types panel */}
      <Dialog open={isManageVendorsOpen} onOpenChange={setIsManageVendorsOpen}>
        <DialogContent data-testid="manage-vendors-dialog">
          <DialogHeader>
            <DialogTitle>Add New Vendor</DialogTitle>
            <DialogDescription>Enter vendor name and select the expense category</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add new vendor */}
            <div className="space-y-3">
              <div>
                <Label>Vendor Name</Label>
                <Input
                  placeholder="Enter vendor name"
                  value={newVendorName}
                  onChange={(e) => setNewVendorName(e.target.value)}
                  data-testid="new-vendor-input"
                />
              </div>
              <div>
                <Label>Category (Required)</Label>
                <Select
                  value={newVendorCategory}
                  onValueChange={setNewVendorCategory}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleAddVendor}
                  data-testid="add-vendor-button"
                  className="flex-1"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vendor
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setIsManageVendorsOpen(false);
                    setNewVendorName('');
                    setNewVendorCategory('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>

            {/* List of existing vendors */}
            {expenseVendors.length > 0 && (
              <div className="border-t pt-4">
                <Label className="text-sm font-medium mb-2 block">Your Vendors</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {expenseVendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                      data-testid={`vendor-item-${vendor.name}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900">{vendor.name}</span>
                        <span className="text-xs text-slate-500">Category: {vendor.category}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteVendor(vendor.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-vendor-${vendor.name}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Expense Categories Dialog - disabled, Super Admin only */}
      {false && (
        <Dialog open={false} onOpenChange={() => {}}>
          <DialogContent data-testid="manage-expense-categories-dialog">
            <DialogHeader>
              <DialogTitle>Manage Expense Categories</DialogTitle>
              <DialogDescription>Add or remove expense category options</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Add new category */}
              <div className="flex gap-2">
                <Input
                  placeholder="Enter new expense category"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  data-testid="new-expense-category-input"
                />
                <Button 
                  onClick={handleAddCategory}
                  data-testid="add-expense-category-button"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* List of categories */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {expenseCategories.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No expense categories yet</p>
                ) : (
                  expenseCategories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                      data-testid={`expense-category-item-${category.name}`}
                    >
                      <span className="font-medium text-slate-900">{category.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(category.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-expense-category-${category.name}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Receipt Viewer Dialog */}
      <Dialog open={showReceiptViewer} onOpenChange={(open) => {
        setShowReceiptViewer(open);
        if (!open && currentReceiptUrl) {
          // Clean up blob URL when closing dialog
          window.URL.revokeObjectURL(currentReceiptUrl);
          setCurrentReceiptUrl('');
          setCurrentReceiptFilename('');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center overflow-auto" style={{ minHeight: '400px' }}>
            {currentReceiptUrl && (
              <>
                {currentReceiptFilename && currentReceiptFilename.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={currentReceiptUrl}
                    className="w-full h-[70vh] border-0"
                    title="Receipt PDF"
                  />
                ) : (
                  <img 
                    src={currentReceiptUrl} 
                    alt="Receipt" 
                    className="max-w-full h-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Unable to load receipt</text></svg>';
                    }}
                  />
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
